import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useOrcamento } from '../context/OrcamentoContext';
import { ETAPAS_OBRA, ITENS_ESPECIAIS, TIPO_PARA_CUB, TIPOS_CONSTRUCAO, PADROES_CONSTRUCAO } from '../data/etapasObra';
import cubDataDefault from '../data/cubData';
import LogService, { LOG_ACTIONS } from '../services/LogService';

function loadCubData() {
  try {
    const stored = localStorage.getItem('orcai_cub_data');
    if (stored) return JSON.parse(stored);
  } catch { /* fallback */ }
  return cubDataDefault;
}

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function OrcamentoValidacao() {
  const navigate = useNavigate();
  const { analise, tipo: ctxTipo, padrao: ctxPadrao, estado: ctxEstado, setValidacao, setResultado } = useOrcamento();
  const cubValues = useMemo(() => loadCubData(), []);

  useEffect(() => {
    if (!analise) navigate('/orcamento', { replace: true });
  }, [analise, navigate]);

  const [areaTotal, setAreaTotal] = useState(analise?.area_total || 0);
  const [tipo, setTipo] = useState(ctxTipo || 'casa_terrea');
  const [padrao, setPadrao] = useState(ctxPadrao || 'normal');
  const [comodos, setComodos] = useState(analise?.comodos || []);
  const [itensEspeciais, setItensEspeciais] = useState(() => {
    const detected = analise?.itens_especiais || [];
    return ITENS_ESPECIAIS.map((item) => ({
      ...item,
      checked: detected.includes(item.id),
    }));
  });
  const [novoComodo, setNovoComodo] = useState({ nome: '', quantidade: 1, area: '' });

  if (!analise) return null;

  const observacoes = analise.observacoes || [];
  const itensObrigatorios = analise.itens_obrigatorios || [];

  const updateComodo = (index, field, value) => {
    setComodos((prev) => prev.map((c, i) =>
      i === index ? { ...c, [field]: field === 'nome' ? value : Number(value) || 0 } : c
    ));
  };

  const removeComodo = (index) => {
    setComodos((prev) => prev.filter((_, i) => i !== index));
  };

  const addComodo = () => {
    if (!novoComodo.nome.trim()) return;
    setComodos((prev) => [...prev, {
      nome: novoComodo.nome.trim(),
      quantidade: Number(novoComodo.quantidade) || 1,
      area: Number(novoComodo.area) || 0,
    }]);
    setNovoComodo({ nome: '', quantidade: 1, area: '' });
  };

  const toggleItemEspecial = (id) => {
    setItensEspeciais((prev) => prev.map((item) =>
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const recalcArea = () => {
    const total = comodos.reduce((sum, c) => sum + (c.area * c.quantidade), 0);
    setAreaTotal(Math.round(total * 10) / 10);
  };

  const handleConfirm = () => {
    const estado = ctxEstado;
    const cubTipo = TIPO_PARA_CUB[tipo]?.[padrao] || 'R1-N';
    const estadoData = cubValues.estados[estado];
    if (!estadoData || !estadoData.tipos[cubTipo]) {
      alert('Valor CUB nao disponivel para este estado/tipo. Usando SP como referencia.');
    }
    const cubValor = estadoData?.tipos[cubTipo]?.valor || cubValues.estados['SP']?.tipos[cubTipo]?.valor || 2131.81;
    const valorBase = areaTotal * cubValor;
    const etapas = ETAPAS_OBRA.map((e) => ({
      ...e,
      valor: valorBase * e.percentual / 100,
    }));
    const itensSelecionados = itensEspeciais
      .filter((i) => i.checked)
      .map((i) => ({ id: i.id, nome: i.nome, valor: i.valor }));
    const somaItensEspeciais = itensSelecionados.reduce((s, i) => s + i.valor, 0);
    const valorTotal = valorBase + somaItensEspeciais;
    const valorM2 = areaTotal > 0 ? valorTotal / areaTotal : 0;

    const resultado = {
      tipo, padrao, estado, cubTipo, cubValor, areaTotal,
      valorBase, valorTotal, valorM2, etapas,
      itensEspeciais: itensSelecionados, somaItensEspeciais, comodos,
    };

    setValidacao({
      area_total: areaTotal, comodos,
      itens_especiais: itensEspeciais.filter((i) => i.checked).map((i) => i.id),
      itens_obrigatorios: itensObrigatorios, observacoes,
    });
    setResultado(resultado);

    LogService.log(LOG_ACTIONS.ORCAMENTO_CALCULO, {
      details: { tipo, padrao, estado, areaTotal, valorTotal },
    });

    navigate('/orcamento/resultado');
  };

  const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-navy mb-2 font-heading">Validar Analise</h1>
          <p className="text-gray-500">Revise e ajuste os dados detectados pela IA antes de calcular o orcamento</p>
        </div>

        {/* Section 1: Resumo */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <h2 className="text-base font-bold text-navy mb-4 flex items-center gap-2 font-heading">
            <svg className="w-5 h-5 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Resumo
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Area Total (m2)</label>
              <div className="flex gap-2">
                <input
                  type="number" min="0" step="0.1"
                  value={areaTotal}
                  onChange={(e) => setAreaTotal(Number(e.target.value) || 0)}
                  className={inputClass}
                />
                <button
                  onClick={recalcArea}
                  title="Recalcular a partir dos comodos"
                  className="px-3 py-2 text-xs font-medium text-brand-blue bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Somar
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputClass}>
                {TIPOS_CONSTRUCAO.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Padrao</label>
              <select value={padrao} onChange={(e) => setPadrao(e.target.value)} className={inputClass}>
                {PADROES_CONSTRUCAO.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: Comodos */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <h2 className="text-base font-bold text-navy mb-4 flex items-center gap-2 font-heading">
            <svg className="w-5 h-5 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Comodos Detectados
          </h2>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500">Comodo</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 w-20">Qtd</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 w-28">Area (m2)</th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 w-24">Subtotal</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {comodos.map((c, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 px-2">
                      <input type="text" value={c.nome} onChange={(e) => updateComodo(i, 'nome', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="number" min="1" value={c.quantidade} onChange={(e) => updateComodo(i, 'quantidade', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm text-center rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="number" min="0" step="0.1" value={c.area} onChange={(e) => updateComodo(i, 'area', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm text-center rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue" />
                    </td>
                    <td className="py-2 px-2 text-center text-sm text-gray-600 font-medium">
                      {(c.quantidade * c.area).toFixed(1)} m2
                    </td>
                    <td className="py-2 px-1">
                      <button onClick={() => removeComodo(i)} className="p-1 text-gray-300 hover:text-red-500 cursor-pointer transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <input type="text" placeholder="Nome do comodo" value={novoComodo.nome}
                onChange={(e) => setNovoComodo((p) => ({ ...p, nome: e.target.value }))}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-blue" />
            </div>
            <div className="w-16">
              <input type="number" min="1" placeholder="Qtd" value={novoComodo.quantidade}
                onChange={(e) => setNovoComodo((p) => ({ ...p, quantidade: e.target.value }))}
                className="w-full px-2 py-2 text-sm text-center rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-blue" />
            </div>
            <div className="w-24">
              <input type="number" min="0" step="0.1" placeholder="Area m2" value={novoComodo.area}
                onChange={(e) => setNovoComodo((p) => ({ ...p, area: e.target.value }))}
                className="w-full px-2 py-2 text-sm text-center rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-blue" />
            </div>
            <button onClick={addComodo} disabled={!novoComodo.nome.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-blue-600 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              Adicionar
            </button>
          </div>
        </div>

        {/* Section 3: Itens Especiais */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <h2 className="text-base font-bold text-navy mb-4 flex items-center gap-2 font-heading">
            <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Itens Especiais
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {itensEspeciais.map((item) => (
              <div key={item.id} onClick={() => toggleItemEspecial(item.id)}
                className={`border rounded-xl p-3 transition-all cursor-pointer flex items-center gap-3 ${
                  item.checked ? 'border-brand-orange bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  item.checked ? 'bg-brand-orange border-brand-orange' : 'border-gray-300'
                }`}>
                  {item.checked && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{item.nome}</p>
                  <p className="text-xs text-gray-400">{formatBRL(item.valor)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 4: Itens Obrigatorios */}
        {itensObrigatorios.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
            <h2 className="text-base font-bold text-navy mb-4 flex items-center gap-2 font-heading">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Itens Obrigatorios
            </h2>
            <ul className="space-y-2">
              {itensObrigatorios.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section 5: Observacoes do Bruno */}
        {observacoes.length > 0 && (
          <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <img src="/images/brand/bruno-tablet.png" alt="Bruno" className="w-10 h-10 rounded-full object-cover" />
              <h2 className="text-base font-bold text-navy font-heading">Observacoes do Bruno</h2>
            </div>
            <ul className="space-y-2">
              {observacoes.map((obs, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-navy">
                  <span className="text-brand-blue mt-0.5">-</span>
                  {obs}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button onClick={() => navigate('/orcamento')}
            className="flex-1 py-4 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
            Voltar
          </button>
          <button onClick={handleConfirm} disabled={areaTotal <= 0}
            className="flex-[2] py-4 text-base font-bold text-white bg-brand-orange rounded-xl hover:bg-brand-orange-dark cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-brand-orange/20">
            Confirmar e Calcular
          </button>
        </div>
      </main>
    </div>
  );
}

export default OrcamentoValidacao;
